import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import useOrderSummary from '../hooks/useOrderSummary';
import Spin from 'components/Spin';
import cart from '../cart';
import { styled } from '@mui/material/styles';
import { Card, Row } from '../styled';
import Typography from '@mui/material/Typography';

const Sidebar = styled(Box)(
  ({ theme }) => `
  background-color: ${theme.palette.primary.main};
  &:before {
    background-image: radial-gradient(120% 40%, #04abff, ${theme.palette.primary.main} 100%);
  }   

  > .sidebar-content{
    max-height: 100%;
    overflow: auto;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      width: 0;
      height: 0;
      background: transparent;
    }
    ::-webkit-scrollbar-thumb {
      background: transparent;
    }
  }
`,
);

export default () => {
  const [t] = useTranslation('checkout');
  if (global.IS_SERVER) return null;
  const productId = cart.get('product_id');

  if (!productId) {
    return <Redirect to="/404" />;
  }

  const {
    loading,
    email,
    plan,
    immediate,
    immediateTotal,
    yearly,
    yearlyTotal,
    monthly,
    monthlyTotal,
  } = useOrderSummary(productId);

  if (loading) return <Spin spinning />;

  return (
    <Fragment>
      <Helmet>
        <title>Start Trial</title>
        <style>
          {`
          .embed-container {position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%;} .embed-container
          iframe, .embed-container object, .embed-container
          embed {position: absolute; top: 0; left: 0; width: 100%; height: 100%;}
          `}
        </style>
      </Helmet>
      <Box className="logo" textAlign="center" pb={4}>
        <a href="/">
          <img src={`/assets/svg/tic-logo-full.svg`} alt="logo" />
        </a>
      </Box>
      <Card sx={{ maxWidth: 760, p: 4, mx: 'auto' }}>
        <Typography paragraph textAlign="center" variant="h1">
          Congrats - Order Complete!
        </Typography>
        <Typography paragraph textAlign="center">
          We're creating your account now. <br />You will receive an email shortly
          with your login credentials.
        </Typography>
        <Box mb={3}>
          <div className="embed-container">
            <iframe
              src={t('order-complete-video')}
              frameBorder="0"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen="true"
            />
          </div>
        </Box>
        <Typography textAlign="center" sx={{ mb: 4 }}>
          Your login information was sent to <br />
          <strong>{email}</strong>
        </Typography>
        <div className="checkout-cart">
          <Row>
            <strong>Your Subscription</strong>
          </Row>
          <Row>
            <span>{plan.name}</span>
            <strong>
              ${plan.price} per {plan.freq}
            </strong>
          </Row>
          {immediate.map((o) => (
            <Row>
              <span>{o.offer_statement_title}</span>{' '}
              <strong>${o.offer_cost} today</strong>
            </Row>
          ))}
          {monthly.map((o) => (
            <Row>
              <span>{o.product_name}</span>{' '}
              <strong>${Number(o.product_amount)} per month</strong>
            </Row>
          ))}
          {yearly.map((o) => (
            <Row>
              <span>{o.product_name}</span>{' '}
              <strong>${Number(o.product_amount)} per year</strong>
            </Row>
          ))}
          <Box p="10px">
            <span>
              <strong>Total:</strong>
            </span>{' '}
            <span className="pull-right-md">
              {immediateTotal ? `$${immediateTotal} today` : ''}
              {immediateTotal && (monthlyTotal || yearlyTotal)
                ? ' and then '
                : ''}
              {monthlyTotal ? `$${monthlyTotal} per month ` : ''}
              {yearlyTotal ? `$${yearlyTotal} per year ` : ''}
              {plan.trialDuration
                ? `(after your ${plan.trialDuration} day trial)`
                : ''}
            </span>
          </Box>
        </div>
      </Card>
    </Fragment>
  );
};
